/**
 * Visor de fotos de adjuntos con botones para rotarlas y hacer zoom.
 *
 * Muchas boletas se fotografían de lado; el navegador muestra la imagen tal
 * cual y no hay forma de enderezarla. La rotación y el zoom son solo visuales
 * (CSS): no se modifica el archivo guardado.
 *
 * El zoom del navegador no sirve aquí: la foto se ajusta al tamaño de la
 * ventana, así que al agrandar todo se vuelve a encoger. Por eso el visor
 * tiene su propio zoom (botones o rueda del mouse) y se arrastra para moverla.
 *
 * La pestaña parte vacía (sin los estilos ni el JS del admin), así que estilos
 * y script van en línea. El script se inyecta en la propia pestaña para que los
 * botones sigan funcionando aunque se cierre el admin que la abrió.
 */

const STYLES = `
  * { box-sizing: border-box; }
  html, body { height: 100%; margin: 0; }
  body {
    display: flex; flex-direction: column;
    background: #111827;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .toolbar {
    display: flex; justify-content: center; gap: 8px;
    padding: 10px 16px;
    background: #1F2937;
  }
  .toolbar button {
    padding: 8px 14px;
    border: 1px solid #374151; border-radius: 8px;
    background: #FFFFFF; color: #104B94;
    font: inherit; font-size: 14px; font-weight: 600;
    cursor: pointer;
  }
  .toolbar button:hover { background: #E8F0FB; }
  .toolbar .zoom-level {
    min-width: 56px; align-self: center; text-align: center;
    color: #D1D5DB; font-size: 14px;
  }
  .stage {
    flex: 1; min-height: 0;
    display: flex; align-items: center; justify-content: center;
    overflow: hidden;
    touch-action: none;
  }
  .stage img {
    max-width: 100%; max-height: 100%;
    transition: transform 0.2s ease;
    user-select: none; -webkit-user-drag: none;
  }
  .stage img.zoomed { cursor: grab; }
  .stage img.dragging { cursor: grabbing; transition: none; }
  .stage img.loading { display: none; }
  .status { color: #D1D5DB; font-size: 14px; text-align: center; }
  .status[hidden] { display: none; }
  .spinner {
    width: 40px; height: 40px; margin: 0 auto 14px;
    border: 4px solid #374151;
    border-top-color: #FFFFFF;
    border-radius: 50%;
    animation: spin 0.9s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) {
    .spinner { animation-duration: 2.4s; }
  }
  /* De lado, el ancho de la foto ocupa el alto de la pantalla y viceversa. */
  .stage img.sideways { max-width: calc(100vh - 56px); max-height: 100vw; }
`;

const MARKUP = `
  <div class="toolbar">
    <button type="button" id="rotate-left" title="Rotar a la izquierda">&#x21BA; Izquierda</button>
    <button type="button" id="rotate-right" title="Rotar a la derecha">Derecha &#x21BB;</button>
    <button type="button" id="zoom-out" title="Alejar">&minus;</button>
    <span class="zoom-level" id="zoom-level">100%</span>
    <button type="button" id="zoom-in" title="Acercar">+</button>
    <button type="button" id="zoom-reset" title="Ajustar a la ventana">Ajustar</button>
  </div>
  <div class="stage" id="image-stage">
    <div class="status" id="image-status" role="status" aria-live="polite">
      <div class="spinner" id="image-spinner" aria-hidden="true"></div>
      <span id="image-status-text">Cargando la foto…</span>
    </div>
    <img id="attachment-image" class="loading" alt="Adjunto">
  </div>
`;

const SCRIPT = `
  (function () {
    var image = document.getElementById('attachment-image');
    var status = document.getElementById('image-status');
    var degrees = 0;
    function showImage() {
      status.hidden = true;
      image.classList.remove('loading');
    }
    function showError() {
      document.getElementById('image-spinner').hidden = true;
      document.getElementById('image-status-text').textContent = 'No se pudo cargar la foto.';
    }
    if (image.complete && image.naturalWidth) {
      showImage();
    } else {
      image.addEventListener('load', showImage);
      image.addEventListener('error', showError);
    }
    var stage = document.getElementById('image-stage');
    var zoomLevel = document.getElementById('zoom-level');
    var MIN_SCALE = 1;
    var MAX_SCALE = 8;
    var scale = 1;
    var offsetX = 0;
    var offsetY = 0;
    var drag = null;
    function applyTransform() {
      image.style.transform = 'translate(' + offsetX + 'px, ' + offsetY + 'px) rotate(' + degrees + 'deg) scale(' + scale + ')';
      image.classList.toggle('sideways', degrees % 180 !== 0);
      image.classList.toggle('zoomed', scale > 1);
      zoomLevel.textContent = Math.round(scale * 100) + '%';
    }
    function rotate(step) {
      degrees = (degrees + step + 360) % 360;
      applyTransform();
    }
    // Acerca manteniendo fijo el punto (x, y) de la pantalla, p. ej. el cursor.
    function zoomAt(factor, x, y) {
      var next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * factor));
      if (next === MIN_SCALE) {
        offsetX = 0;
        offsetY = 0;
      } else {
        var rect = stage.getBoundingClientRect();
        var px = x - (rect.left + rect.width / 2);
        var py = y - (rect.top + rect.height / 2);
        offsetX = px - (px - offsetX) * (next / scale);
        offsetY = py - (py - offsetY) * (next / scale);
      }
      scale = next;
      applyTransform();
    }
    function zoomAtCenter(factor) {
      var rect = stage.getBoundingClientRect();
      zoomAt(factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }
    function resetZoom() {
      scale = 1;
      offsetX = 0;
      offsetY = 0;
      applyTransform();
    }
    document.getElementById('rotate-left').addEventListener('click', function () { rotate(-90); });
    document.getElementById('rotate-right').addEventListener('click', function () { rotate(90); });
    document.getElementById('zoom-in').addEventListener('click', function () { zoomAtCenter(1.25); });
    document.getElementById('zoom-out').addEventListener('click', function () { zoomAtCenter(0.8); });
    document.getElementById('zoom-reset').addEventListener('click', resetZoom);
    stage.addEventListener('wheel', function (event) {
      event.preventDefault();
      zoomAt(event.deltaY < 0 ? 1.15 : 1 / 1.15, event.clientX, event.clientY);
    }, { passive: false });
    image.addEventListener('pointerdown', function (event) {
      if (scale === 1) return;
      drag = { x: event.clientX - offsetX, y: event.clientY - offsetY };
      image.setPointerCapture(event.pointerId);
      image.classList.add('dragging');
    });
    image.addEventListener('pointermove', function (event) {
      if (!drag) return;
      offsetX = event.clientX - drag.x;
      offsetY = event.clientY - drag.y;
      applyTransform();
    });
    function endDrag() {
      drag = null;
      image.classList.remove('dragging');
    }
    image.addEventListener('pointerup', endDrag);
    image.addEventListener('pointercancel', endDrag);
  })();
`;

/**
 * Reemplaza el contenido de la pestaña por el visor de la foto.
 *
 * @param {Window} tab - Pestaña abierta con window.open('').
 * @param {string} src - URL de la imagen (firmada o blob local).
 */
export function renderImageViewerPage(tab, src) {
  const doc = tab.document;
  doc.title = 'Adjunto';
  doc.head.innerHTML = '';
  const style = doc.createElement('style');
  style.textContent = STYLES;
  doc.head.appendChild(style);
  // Solo contenido estático: la URL entra por la propiedad, no por el HTML.
  doc.body.innerHTML = MARKUP;
  doc.getElementById('attachment-image').src = src;
  const script = doc.createElement('script');
  script.textContent = SCRIPT;
  doc.body.appendChild(script);
}
